import React, { Reducer, useReducer, useState } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import useUserContext from '../../../providers/User/UserProvider';

import {
  getPricingSheets,
  getPricingSheetsAll,
} from '../../../services/pricingSheets';

import {
  PricingSheet,
  PricingSheetState,
  TableColumn,
  TablePaginationOptions,
  TableSortOptions,
  TableSortOrder,
} from '../../../../../shared/types';

import { DataTable } from '../../../components/Common/DataTable/DataTable';

import { PricingSheetsSummaryTable } from './PricingSheetsSummaryTable';
import PricingSheetsTableRowDetails from './PricingSheetsTableRowDetails';
import PricingSheetsTableRowActions from './PricingSheetsTableRowActions';
import { useDataTableFiltering } from '../../../hooks/useDataTableFiltering';
import {
  colors,
  PRICING_SHEET_ALL_QUERY_KEY,
  PRICING_SHEET_QUERY_KEY,
  TABLE_SIZE_OPTIONS,
} from '../../../../../shared/constants';
import {
  GetPricingSheetsTableFilterOptions,
  PricingSheetTableData,
} from '../../../../../shared/types/pricingSheets';
import { OffersTableTabState } from '../../../../../shared/types/offers';
import { produce } from 'immer';
import { PricingSheetTableColumnFilter } from './PricingSheetTableColumnFilter';
import GenericContainer from '../../../GenericContainer';
import { PricingSheetTableCellComponent } from './PricingSheetTableCellComponent';
import { useQuery } from '@tanstack/react-query';
import TopLevelFilters from './TopLevelFilters';
import { addDays } from 'date-fns';
import isPricingSheetExpired from '../../../../../shared/helpers/isPricingSheetExpired';
import isPricingSheetExpiring from '../../../../../shared/helpers/isPricingSheetExpiring';
import { getLocalStorageItem } from '../../../helpers/getLocalStorageItem';
import { isResponsiblePerson } from '../../../../../shared/helpers/isResponsiblePerson';

const TABLE_COLUMNS: TableColumn<PricingSheetTableData>[] = [
  { key: 'id', label: 'Työkalun id', width: 100 },
  { key: 'name', label: 'Hinnaston nimi', width: 400 },
  { key: 'customerName', label: 'Asiakas' },
  { key: 'site', label: 'Työmaa' },
  { key: 'responsiblePerson', label: 'Vastuullinen' },
  { key: 'state', label: 'Tila' },
  { key: 'createdAt', label: 'Luotu' },
  { key: 'to', label: 'Voimassa asti' },
];

const DEFAULT_FILTER: GetPricingSheetsTableFilterOptions = {
  byUser: true,
  validTo: undefined,
  archived: false,
  includeStates: [
    PricingSheetState.Approved,
    PricingSheetState.Draft,
    PricingSheetState.InUse,
    PricingSheetState.OkDraft,
    PricingSheetState.WarningDraft,
    PricingSheetState.Pending,
    PricingSheetState.Ready,
  ],
};

const DEFAULT_PAGINATION: TablePaginationOptions = {
  limit: TABLE_SIZE_OPTIONS[3],
  offset: 0,
};

const DEFAULT_SORT: TableSortOptions<PricingSheetTableData> = {
  direction: TableSortOrder.Asc,
  orderBy: 'to',
};

type GetPricingSheetsTableToggleStateOptions = {
  checked: Boolean;
  toggleState: PricingSheetState;
};

type ExpiredOptions = 'all' | 'notExpired' | 'expiring' | 'expired';

type GetPricingSheetsTableToggleExpiredOptions = {
  toggleExpired: ExpiredOptions;
};
type GetPricingSheetsTableToggleArchivedOptions = {
  toggleArchived: 'all' | 'notArchived' | 'archived';
};

type GetPricingSheetsTableToggleSharedOptions = {
  toggleShared: 'all' | 'notShared' | 'shared';
};

type TableState = {
  filterState: GetPricingSheetsTableFilterOptions;
  paginationState: TablePaginationOptions;
  sortState: TableSortOptions<PricingSheetTableData>;
};

type TableAction =
  | {
      type: 'SET_ACTIVE_TAB';
      payload: OffersTableTabState;
    }
  | {
      type: 'SET_FILTER';
      payload: GetPricingSheetsTableFilterOptions;
    }
  | {
      type: 'SET_PAGINATION';
      payload: TablePaginationOptions;
    }
  | {
      type: 'SET_SORT';
      payload: TableSortOptions<PricingSheetTableData>;
    }
  | {
      type: 'TOGGLE_STATE';
      payload: GetPricingSheetsTableToggleStateOptions;
    }
  | {
      type: 'TOGGLE_EXPIRED_FILTER';
      payload: GetPricingSheetsTableToggleExpiredOptions;
    }
  | {
      type: 'TOGGLE_ARCHIVED_FILTER';
      payload: GetPricingSheetsTableToggleArchivedOptions;
    }
  | {
      type: 'TOGGLE_SHARED_FILTER';
      payload: GetPricingSheetsTableToggleSharedOptions;
    };

const INITIAL_STATE: TableState = {
  filterState: {
    ...DEFAULT_FILTER,
  },
  paginationState: DEFAULT_PAGINATION,
  sortState: DEFAULT_SORT,
};

const reducer: Reducer<TableState, TableAction> = produce(
  (state, { type, payload }) => {
    switch (type) {
      case 'SET_FILTER':
        state.filterState = payload;
        state.paginationState.offset = 0;
        break;
      case 'SET_PAGINATION':
        state.paginationState = payload;
        break;
      case 'SET_SORT':
        state.sortState = payload;
        break;
      case 'TOGGLE_STATE': {
        const { toggleState, checked } = payload;

        let includeStates = state.filterState.includeStates ?? [];

        if (checked) {
          includeStates = Array.from(
            new Set([...includeStates, toggleState]),
          );
        } else {
          includeStates = includeStates.filter(
            (s) => s !== toggleState,
          );
        }
        state.filterState.includeStates = includeStates;
        state.paginationState.offset = 0;
        break;
      }
      case 'TOGGLE_EXPIRED_FILTER': {
        const { toggleExpired } = payload;

        switch (toggleExpired) {
          case 'all': {
            state.filterState.validTo = undefined;
            break;
          }
          case 'notExpired': {
            state.filterState.validTo = `${new Date()}|gte`;
            break;
          }
          case 'expiring': {
            const queryString = `${new Date()}~${addDays(
              new Date(),
              60,
            )}|between`;
            state.filterState.validTo = queryString;
            break;
          }
          case 'expired': {
            state.filterState.validTo = `${new Date()}|lte`;
            break;
          }
        }
        state.paginationState.offset = 0;
        break;
      }
      case 'TOGGLE_ARCHIVED_FILTER': {
        const { toggleArchived } = payload;

        switch (toggleArchived) {
          case 'all': {
            delete state.filterState.archived;
            break;
          }
          case 'notArchived': {
            state.filterState.archived = false;
            break;
          }
          case 'archived': {
            state.filterState.archived = true;
            break;
          }
        }
        state.paginationState.offset = 0;
        break;
      }
      case 'TOGGLE_SHARED_FILTER': {
        const { toggleShared } = payload;

        switch (toggleShared) {
          case 'all': {
            delete state.filterState.shared;
            break;
          }
          case 'notShared': {
            state.filterState.shared = false;
            break;
          }
          case 'shared': {
            state.filterState.shared = true;
            break;
          }
        }
        state.paginationState.offset = 0;
        break;
      }
      default:
        return state;
    }
  },
);

const Pricings = () => {
  const { userEmail } = useUserContext();
  const [state, dispatch] = useReducer(reducer, {
    ...INITIAL_STATE,
    ...{
      sortState: {
        orderBy:
          getLocalStorageItem<keyof PricingSheetTableData>(
            'pricingSheetTableDefaultOrderBy',
          ) || DEFAULT_SORT.orderBy,
        direction:
          getLocalStorageItem<TableSortOrder>(
            'pricingSheetTableDefaultSortDirection',
          ) || DEFAULT_SORT.direction,
      },
    },
  });
  const { filterState, paginationState, sortState } = state;
  const [expirationState, setExpirationState] =
    useState<ExpiredOptions>('all');
  const {
    refetch: refetchPricingSheetsAll,
    data: { items: pricingSheetAll = [] } = {},
  } = useQuery<any>({
    queryKey: [PRICING_SHEET_ALL_QUERY_KEY],
    queryFn: async () => {
      return getPricingSheetsAll();
    },
  });
  const { query } = useDataTableFiltering<
    PricingSheetTableData,
    GetPricingSheetsTableFilterOptions
  >({
    queryOptions: {
      queryKey: [PRICING_SHEET_QUERY_KEY],
      queryFn: getPricingSheets as any, // todo fix type
    },
    filterOptions: filterState,
    paginationOptions: paginationState,
    sortOptions: sortState,
  });

  const {
    refetch: refetchPricingSheets,
    data: { count: itemCount = 0, items: pricingSheets = [] } = {},
    isFetching,
  } = query;
  // ***
  // Mira customer's blocking info fetch is temporary disabled due to bug

  // const customerIds = pricingSheets
  //   .map((pricingSheet) => pricingSheet.customerId)
  //   .join(',');
  // const {
  //   data: miraCustomers = [],
  //   isError: isErrorCustomers,
  //   isFetching: isFetchingMiraCustomer,
  // } = useQuery({
  //   queryKey: [MIRA_CUSTOMER_BY_ID_QUERY_KEY, customerIds],
  //   enabled: pricingSheets.length > 0,
  //   queryFn: async () => getMiraCustomerByIds(customerIds),
  // });

  const isFetchingMiraCustomer = false;
  const miraCustomers: any = [];

  const isLoadingCombined = isFetching || isFetchingMiraCustomer;
  // Convert id from string to number
  const pricingSheetsTransformed = pricingSheets.map((item) => ({
    ...item,
    id: Number(item.id),
  }));

  const refetchEverything = async () => {
    await refetchPricingSheets();
    await refetchPricingSheetsAll();
  };

  const setStateFilter = (
    checked: Boolean,
    toggleState: PricingSheetState,
  ) => {
    dispatch({
      type: 'TOGGLE_STATE',
      payload: { toggleState, checked },
    });
  };

  const isStateChecked = (state: PricingSheetState) =>
    filterState.includeStates?.includes(state);

  const stateCountLabel = (states: PricingSheetState[]) => {
    let sheets = filterState.byUser
      ? pricingSheetAll.filter(
          (sheet: PricingSheet) =>
            isResponsiblePerson(sheet, userEmail) ||
            (sheet.editingRights &&
              userEmail &&
              sheet.editingRights.includes(userEmail)),
        )
      : pricingSheetAll;

    // Only shared to user
    if (filterState.shared) {
      sheets = sheets.filter((sheet: PricingSheet) => {
        return (
          sheet.editingRights &&
          userEmail &&
          sheet.editingRights.includes(userEmail)
        );
      });
    }

    // Don't include shared
    if (filterState.shared === false) {
      sheets = sheets.filter((sheet: PricingSheet) => {
        return isResponsiblePerson(sheet, userEmail);
      });
    }

    if (filterState.archived) {
      sheets = sheets.filter((sheet: PricingSheet) => {
        return sheet.archivedAt;
      });
    }
    if (filterState.archived === false) {
      sheets = sheets.filter((sheet: PricingSheet) => {
        return !sheet.archivedAt;
      });
    }

    if (expirationState === 'expired') {
      sheets = sheets.filter(isPricingSheetExpired);
    }

    if (expirationState === 'expiring') {
      sheets = sheets.filter(isPricingSheetExpiring);
    }

    if (expirationState === 'notExpired') {
      sheets = sheets.filter((sheet: PricingSheet) => {
        return !isPricingSheetExpired(sheet);
      });
    }

    return ` (${
      sheets.filter((pricingSheet: PricingSheet) =>
        states.includes(pricingSheet.state),
      ).length
    })`;
  };

  return (
    <GenericContainer borderColor={colors.ramiGreen}>
      <Box marginBottom={3}>
        <Typography
          variant="h4"
          component="div"
          gutterBottom
          color="primary"
        >
          Hinnastot
        </Typography>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
        }}
      >
        <>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'space-between',
            }}
          >
            <TopLevelFilters
              topToggleFilters={[
                {
                  key: 'onlyMine',
                  label: 'Näytä vain omat',
                  checked: filterState.byUser ?? false,
                  onChange: (checked) => {
                    dispatch({
                      type: 'SET_FILTER',
                      payload: {
                        ...filterState,
                        byUser: checked,
                      },
                    });
                  },
                },
              ]}
              onResetFilters={() => {
                setExpirationState('all');
                dispatch({
                  type: 'SET_FILTER',
                  payload: DEFAULT_FILTER,
                });
              }}
              bottomRadioFilters={[
                {
                  key: 'expiration',
                  selectedKey: expirationState,
                  title: 'Voimassaolo',
                  options: [
                    {
                      key: 'all',
                      label: 'Kaikki',
                    },
                    {
                      key: 'notExpired',
                      label: 'Voimassa',
                    },
                    {
                      key: 'expiring',
                      label: 'Vanhentuvat',
                    },
                    {
                      key: 'expired',
                      label: 'Vanhentuneet',
                    },
                  ],
                  onChange: (value: any) => {
                    setExpirationState(value);
                    dispatch({
                      type: 'TOGGLE_EXPIRED_FILTER',
                      payload: { toggleExpired: value },
                    });
                  },
                },
                {
                  key: 'archive',
                  selectedKey:
                    filterState.archived === undefined
                      ? 'all'
                      : filterState?.archived === true
                        ? 'archived'
                        : 'notArchived',
                  title: 'Arkisto',
                  options: [
                    {
                      key: 'all',
                      label: 'Kaikki',
                    },
                    {
                      key: 'notArchived',
                      label: 'Ei arkistoituja',
                    },
                    {
                      key: 'archived',
                      label: 'Vain arkistoidut',
                    },
                  ],
                  onChange: (value: any) => {
                    dispatch({
                      type: 'TOGGLE_ARCHIVED_FILTER',
                      payload: { toggleArchived: value },
                    });
                  },
                },
                {
                  key: 'share',
                  selectedKey:
                    filterState.shared === undefined
                      ? 'all'
                      : filterState?.shared === true
                        ? 'shared'
                        : 'notShared',
                  title: 'Jaetut hinnastot',
                  options: [
                    {
                      key: 'all',
                      label: 'Kaikki',
                    },
                    {
                      key: 'notShared',
                      label: 'Vain omat',
                    },
                    {
                      key: 'shared',
                      label: 'Vain minulle jaetut',
                    },
                  ],
                  onChange: (value: any) => {
                    dispatch({
                      type: 'TOGGLE_SHARED_FILTER',
                      payload: { toggleShared: value },
                    });
                  },
                },
              ]}
              filters={[
                {
                  label:
                    'Näytä kaikki' +
                    stateCountLabel([
                      PricingSheetState.OkDraft,
                      PricingSheetState.WarningDraft,
                      PricingSheetState.Pending,
                      PricingSheetState.Approved,
                      PricingSheetState.Ready,
                      PricingSheetState.InUse,
                    ]),
                  children: [
                    {
                      label:
                        'Keskeneräiset' +
                        stateCountLabel([
                          PricingSheetState.OkDraft,
                          PricingSheetState.WarningDraft,
                          PricingSheetState.Pending,
                          PricingSheetState.Approved,
                          PricingSheetState.Ready,
                        ]),
                      children: [
                        {
                          label:
                            'Luonnos' +
                            stateCountLabel([
                              PricingSheetState.OkDraft,
                              PricingSheetState.WarningDraft,
                            ]),
                          checked: isStateChecked(
                            PricingSheetState.OkDraft,
                          ),
                          onChecked: (checked) => {
                            setStateFilter(
                              checked,
                              PricingSheetState.OkDraft,
                            );
                            setStateFilter(
                              checked,
                              PricingSheetState.WarningDraft,
                            );
                          },
                        },
                        {
                          label:
                            'Odottaa hyväksyntää' +
                            stateCountLabel([
                              PricingSheetState.Pending,
                            ]),
                          checked: isStateChecked(
                            PricingSheetState.Pending,
                          ),
                          onChecked: (checked) =>
                            setStateFilter(
                              checked,
                              PricingSheetState.Pending,
                            ),
                        },
                        {
                          label:
                            'Hyväksytty' +
                            stateCountLabel([
                              PricingSheetState.Approved,
                            ]),
                          checked: isStateChecked(
                            PricingSheetState.Approved,
                          ),
                          onChecked: (checked) =>
                            setStateFilter(
                              checked,
                              PricingSheetState.Approved,
                            ),
                        },
                        {
                          label:
                            'Odottaa vientiä' +
                            stateCountLabel([
                              PricingSheetState.Ready,
                            ]),
                          checked: isStateChecked(
                            PricingSheetState.Ready,
                          ),
                          onChecked: (checked) =>
                            setStateFilter(
                              checked,
                              PricingSheetState.Ready,
                            ),
                        },
                      ],
                    },
                    {
                      label:
                        'Viety Miraan' +
                        stateCountLabel([PricingSheetState.InUse]),
                      children: [
                        {
                          label:
                            'Käytössä' +
                            stateCountLabel([
                              PricingSheetState.InUse,
                            ]),
                          checked: isStateChecked(
                            PricingSheetState.InUse,
                          ),
                          onChecked: (checked) => {
                            setStateFilter(
                              checked,
                              PricingSheetState.InUse,
                            );
                          },
                        },
                      ],
                    },
                  ],
                },
              ]}
            />
            <Box mt={4}>
              <PricingSheetsSummaryTable
                pricingSheets={pricingSheetAll}
                onStateFilterClick={(states: PricingSheetState[]) => {
                  const updatedFilter = {
                    ...filterState,
                    byUser: true,
                    includeStates: states,
                    archived: false,
                  };

                  dispatch({
                    type: 'SET_FILTER',
                    payload: updatedFilter,
                  });

                  setExpirationState('all');
                  dispatch({
                    type: 'TOGGLE_EXPIRED_FILTER',
                    payload: { toggleExpired: 'all' },
                  });
                  dispatch({
                    type: 'TOGGLE_SHARED_FILTER',
                    payload: { toggleShared: 'all' },
                  });
                }}
                onExpiringFilterClick={() => {
                  const updatedFilter = {
                    ...filterState,
                    byUser: true,
                    archived: false,
                    includeStates: [PricingSheetState.InUse],
                  };
                  setExpirationState('expiring');
                  dispatch({
                    type: 'SET_FILTER',
                    payload: updatedFilter,
                  });

                  dispatch({
                    type: 'TOGGLE_EXPIRED_FILTER',
                    payload: { toggleExpired: 'expiring' },
                  });
                  dispatch({
                    type: 'TOGGLE_SHARED_FILTER',
                    payload: { toggleShared: 'all' },
                  });
                }}
                onArchiveFilterClick={() => {
                  const updatedFilter = {
                    ...filterState,
                    byUser: true,
                    archived: true,
                    includeStates: [
                      PricingSheetState.Approved,
                      PricingSheetState.Draft,
                      PricingSheetState.InUse,
                      PricingSheetState.OkDraft,
                      PricingSheetState.WarningDraft,
                      PricingSheetState.Pending,
                      PricingSheetState.Ready,
                    ],
                  };
                  setExpirationState('all');
                  dispatch({
                    type: 'SET_FILTER',
                    payload: updatedFilter,
                  });

                  dispatch({
                    type: 'TOGGLE_EXPIRED_FILTER',
                    payload: { toggleExpired: 'all' },
                  });
                  dispatch({
                    type: 'TOGGLE_SHARED_FILTER',
                    payload: { toggleShared: 'all' },
                  });
                }}
                onSharedFilterClick={() => {
                  const updatedFilter = {
                    ...filterState,
                    byUser: true,
                    shared: true,
                    archived: false,
                    includeStates: [
                      PricingSheetState.Approved,
                      PricingSheetState.Draft,
                      PricingSheetState.InUse,
                      PricingSheetState.OkDraft,
                      PricingSheetState.WarningDraft,
                      PricingSheetState.Pending,
                      PricingSheetState.Ready,
                    ],
                  };
                  setExpirationState('all');
                  dispatch({
                    type: 'TOGGLE_EXPIRED_FILTER',
                    payload: { toggleExpired: 'all' },
                  });
                  dispatch({
                    type: 'SET_FILTER',
                    payload: updatedFilter,
                  });
                }}
              />
            </Box>
          </Box>
          <DataTable
            count={itemCount}
            filterOptions={filterState}
            labelNoResults={'Hinnastoja ei löytynyt!'}
            labelRowsPerPage={'Hinnastoja per sivu'}
            loading={isLoadingCombined}
            miraCustomers={miraCustomers}
            items={pricingSheetsTransformed}
            paginationOptions={paginationState}
            renderCellComponent={PricingSheetTableCellComponent}
            renderRowActions={(renderProps) =>
              PricingSheetsTableRowActions({
                ...renderProps,
                updateSheets: refetchEverything,
              })
            }
            renderColumnFilter={PricingSheetTableColumnFilter}
            renderRowDetails={(renderProps) => {
              return PricingSheetsTableRowDetails({
                pricingSheet: renderProps as any,
                refetchEverything,
              });
            }}
            setFilterOptions={(updatedFilter: any) => {
              const finalFilter =
                typeof updatedFilter === 'function'
                  ? updatedFilter(filterState)
                  : updatedFilter;

              dispatch({
                type: 'SET_FILTER',
                payload: finalFilter,
              });
            }}
            setPaginationOptions={(updatedPagination) => {
              dispatch({
                type: 'SET_PAGINATION',
                payload: updatedPagination,
              });
            }}
            setSortOptions={(updatedSort) => {
              localStorage.setItem(
                'pricingSheetTableDefaultOrderBy',
                updatedSort.orderBy,
              );
              localStorage.setItem(
                'pricingSheetTableDefaultSortDirection',
                updatedSort.direction,
              );
              dispatch({
                type: 'SET_SORT',
                payload: updatedSort,
              });
            }}
            sortOptions={sortState}
            tableColumns={TABLE_COLUMNS}
            tableHeaderColor={undefined}
          />
        </>
      </Box>
    </GenericContainer>
  );
};

export default Pricings;
